import { useSyncExternalStore } from "react";

/**
 * What onboarding asks production to hide, lock or highlight while a tour
 * runs. Every field has exactly one production reader; the tour runner is
 * the only writer.
 */
export interface Presentation {
  /** Canvas page: pan and zoom off. */
  viewportLocked: boolean;
  /** File publish button: glow, and close the popover when it moves off. */
  spotlightTarget: string | null;
  /** Canvas floating chat panel: no launcher, no landing. */
  canvasChatHidden: boolean;
  /** Canvas session pane: the tour session's composer opens holding this. */
  composerPinnedPrompt: string | null;
  /** Canvas session pane: no empty-state suggestions. */
  suggestionsHidden: boolean;
}

export const DEFAULT_PRESENTATION: Presentation = {
  viewportLocked: false,
  spotlightTarget: null,
  canvasChatHidden: false,
  composerPinnedPrompt: null,
  suggestionsHidden: false,
};

type Patch = Partial<Presentation>;

// Patches stack in application order; the newest writer of a field wins and
// releasing one restores whatever the remaining writers say.
let patches: Patch[] = [];
let state: Presentation = DEFAULT_PRESENTATION;
const listeners = new Set<() => void>();

function recompute() {
  const next = { ...DEFAULT_PRESENTATION };
  for (const patch of patches) Object.assign(next, patch);
  const changed = (Object.keys(next) as (keyof Presentation)[]).some(
    (k) => next[k] !== state[k],
  );
  if (!changed) return;
  state = next;
  listeners.forEach((l) => l());
}

/** Apply a patch; the returned function releases it. */
export function setPresentation(patch: Patch): () => void {
  patches = [...patches, patch];
  recompute();
  return () => {
    patches = patches.filter((p) => p !== patch);
    recompute();
  };
}

export function getPresentation(): Presentation {
  return state;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePresentation<K extends keyof Presentation>(
  field: K,
): Presentation[K] {
  return useSyncExternalStore(
    subscribe,
    () => state[field],
    () => DEFAULT_PRESENTATION[field],
  );
}

export function resetPresentationForTest() {
  patches = [];
  recompute();
}
